import withKeyboardFittingElement from 'HOCs/withKeyboardFittingElement';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import {
  CONFIG_OCCUPATION_EXTRAS,
  DESKTOP_MIN_WIDTH,
  FUNC_DEFAULT,
} from 'constants';
import withFormPIIProfilePersonalDetailsInitial from 'features/pii/HOCs/withFormPIIProfilePersonalDetailsInitial';
import {
  PII_PROFILE_PERSONAL_DETAILS_DOB_FIELD,
  PII_PROFILE_PERSONAL_DETAILS_FISTNAME_FIELD,
  PII_PROFILE_PERSONAL_DETAILS_FISTNAME_NAME,
  PII_PROFILE_PERSONAL_DETAILS_GENDER_FIELD,
  PII_PROFILE_PERSONAL_DETAILS_LASTNAME_FIELD,
  PII_PROFILE_PERSONAL_DETAILS_LASTNAME_NAME,
  PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_FIELD,
  PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME,
  PII_PROFILE_PERSONAL_DETAILS_OCCUPATION_NAME,
  PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME,
} from 'features/pii/const';
import { checkFormInputValidation } from 'features/pii/func';
import usePIIStore from 'features/pii/hooks/usePIIStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { displayFlexRowCenterSpaceBetweenMixin } from 'styles';
import CustomOccupationDropdown from './components/CustomOccupationDropdown';
import FormInputDatepickerValidation from './components/FormInputDatepickerValidation';
import FormInputGenderRadioValidation from './components/FormInputGenderRadioValidation';
import FormInputNameValidation from './components/FormInputNameValidation';

const FormPIIProfilePersonalDetails = withKeyboardFittingElement(
  withFormPIIProfilePersonalDetailsInitial(
    ({
      inputRef,
      currentStep,
      setCurrentStep,
      information,
      setInformation,
      validation,
      setValidation,
      checkAllowSubmit,
    }) => {
      const { t } = useLang();
      const { isDesktop } = useDetectDevice();
      const { isLoading, setPIIProfilePersonalDetails } = usePIIStore();

      const [
        isForceShowRestOfPersonalDetails,
        setForceShowRestOfPersonalDetails,
      ] = useState(false);

      const isNextButtonDisabled =
        isLoading ||
        !checkAllowSubmit({
          information,
          validation,
          skipProps: [
            ...[
              !(
                information[PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME] &&
                checkFormInputValidation(
                  information[PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME],
                  PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_FIELD,
                  information,
                  validation
                )?.[PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME]
              ) && PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_NAME,
            ],
            ...[
              !(
                information[PII_PROFILE_PERSONAL_DETAILS_OCCUPATION_NAME] &&
                CONFIG_OCCUPATION_EXTRAS?.find(
                  (occupation) =>
                    occupation ===
                    information[PII_PROFILE_PERSONAL_DETAILS_OCCUPATION_NAME]
                      ?.key
                )
              ) && PII_PROFILE_PERSONAL_DETAILS_PREVIOUS_EMPLOYED_NAME,
            ],
          ],
        });

      const handleNextStep = async () => {
        if (isNextButtonDisabled) return;
        try {
          console.log(
            `⭐ FormPIIProfilePersonalDetails 👉 handleNextStep ➡️ information ↩`,
            information
          );
          setPIIProfilePersonalDetails(information);
          setCurrentStep(currentStep + 1);
        } catch (error) {
          console.error(
            `🔥 FormPIIProfilePersonalDetails ❌ handleNextStep ➡️ ERROR ↩`,
            error?.message
          );
        }
      };

      const isHiddenRestOfPersonalDetails =
        !information[PII_PROFILE_PERSONAL_DETAILS_FISTNAME_NAME] ||
        !information[PII_PROFILE_PERSONAL_DETAILS_LASTNAME_NAME];
      const renderRestOfPersonalDetails = () => {
        if (isHiddenRestOfPersonalDetails && !isForceShowRestOfPersonalDetails)
          return null;
        return (
          <>
            <SizeBox height={isDesktop ? 40 : 16} />
            <RestOfPersonalDetailsStyled>
              <FormInputGenderRadioValidation
                field={PII_PROFILE_PERSONAL_DETAILS_GENDER_FIELD}
                information={information}
                setInformation={setInformation}
                validation={validation}
                setValidation={setValidation}
              />
              <SizeBox height={16} />
              <FormInputDatepickerValidation
                ref={inputRef}
                field={PII_PROFILE_PERSONAL_DETAILS_DOB_FIELD}
                information={information}
                setInformation={setInformation}
                validation={validation}
                setValidation={setValidation}
              />
              <SizeBox height={16} />
              <CustomOccupationDropdown
                information={information}
                setInformation={setInformation}
                validation={validation}
                setValidation={setValidation}
              />
            </RestOfPersonalDetailsStyled>
            <SizeBox height={isDesktop ? 40 : 0} />
          </>
        );
      };

      useEffect(() => {
        if (
          information[PII_PROFILE_PERSONAL_DETAILS_FISTNAME_NAME] &&
          information[PII_PROFILE_PERSONAL_DETAILS_LASTNAME_NAME]
        ) {
          setForceShowRestOfPersonalDetails(true);
        }
      }, [
        information[PII_PROFILE_PERSONAL_DETAILS_FISTNAME_NAME],
        information[PII_PROFILE_PERSONAL_DETAILS_LASTNAME_NAME],
      ]);

      const renderBottomFixed = () => {
        if (isDesktop && isHiddenRestOfPersonalDetails) return null;
        return (
          <BottomFixedStyled>
            <ButtonCommon
              value={t('button_next')}
              styles={{
                margin: '0px',
                width: '100%',
                height: '50px',
                ...(isDesktop && {
                  width: '200px',
                  marginLeft: 'auto',
                }),
              }}
              color={
                isNextButtonDisabled
                  ? 'var(--ds-c-blue-disabled)'
                  : 'var(--c-primary)'
              }
              background={
                isNextButtonDisabled ? 'var(--ds-bg-4)' : 'var(--bg-primary)'
              }
              onClick={isNextButtonDisabled ? FUNC_DEFAULT : handleNextStep}
              isUseKeyDown
              isFill={true}
            />
          </BottomFixedStyled>
        );
      };

      return (
        <FormPIIProfilePersonalDetailsStyled>
          <FirstMiddleNameSection>
            <FormInputNameValidation
              field={PII_PROFILE_PERSONAL_DETAILS_FISTNAME_FIELD}
              information={information}
              setInformation={setInformation}
              validation={validation}
              setValidation={setValidation}
              checkFormInputValidation={checkFormInputValidation}
              rootStyles={{
                width: 'calc(50% - 6px)',
              }}
            />
            <FormInputNameValidation
              field={PII_PROFILE_PERSONAL_DETAILS_MIDDLENAME_FIELD}
              information={information}
              setInformation={setInformation}
              validation={validation}
              setValidation={setValidation}
              checkFormInputValidation={checkFormInputValidation}
              rootStyles={{
                width: 'calc(50% - 6px)',
              }}
            />
          </FirstMiddleNameSection>
          <SizeBox height={16} />
          <FormInputNameValidation
            field={PII_PROFILE_PERSONAL_DETAILS_LASTNAME_FIELD}
            information={information}
            setInformation={setInformation}
            validation={validation}
            setValidation={setValidation}
            checkFormInputValidation={checkFormInputValidation}
          />
          {renderRestOfPersonalDetails()}
          <SizeBox height={isDesktop ? 0 : 240} />
          {renderBottomFixed()}
        </FormPIIProfilePersonalDetailsStyled>
      );
    }
  )
);

const FormPIIProfilePersonalDetailsStyled = styled.div``;

const FirstMiddleNameSection = styled.div`
  ${displayFlexRowCenterSpaceBetweenMixin};
  align-items: start;
`;

const RestOfPersonalDetailsStyled = styled.div``;

const BottomFixedStyled = styled.div`
  position: fixed;
  bottom: 0;
  left: 0px;
  width: 100%;
  padding: 12px 16px;
  border-top: 1px solid var(--ds-c-grey-disabled);
  background-color: #fff;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    position: unset;
    border-top: unset;
    padding: 0;
  }
`;

export default FormPIIProfilePersonalDetails;
